import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getPaperTexture, getPaperMean } from '../paper-texture.js';

// Shared-rig contract (see the framing addendum). 'forbidden': nothing here
// emits light - see this file's own header.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'shoji',
  family: 'paper',
  energyLevel: 0.10,
  bpmMin: 40,
  bpmMax: 120,
  movement: 0.15,
  danceability: 0.30,
  tonality: 0.92,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.35], density: [0.0, 0.35] },
  enabled: true
};

// Shoji: sliding translucent screens with shadows passing behind them. The
// one visualizer in the library built on OCCLUSION rather than emission -
// nothing here emits light; a diffuse source sits behind the whole scene,
// and everything visible is either what passes through paper or what stops
// at wood. Registry: light background, NORMAL blending, luminance capped at
// 0.74 - same family as sumi.js/halftone.js/loom.js.
//
// ONE full-frame quad, everything computed per-fragment: panels are
// POSITION TESTS against the current pixel's UV, not geometry - there is no
// panel mesh, no muntin mesh, just arithmetic re-run at every pixel. This is
// what keeps 6 sliding, overlapping screens with a 4x7 grid each affordable
// on a Pi.

// --- panels -----------------------------------------------------------
// --- the panels ---------------------------------------------------------
// DEFINED HERE, IN WORLD UNITS, not derived in the shader. Six screens on a
// track, each with its own width, travel and slow independent oscillation.
// Every frame their current [xLeft, xRight] goes to the shader as six vec2 and
// that is the ONLY thing the shader is told about them - which is what makes
// the lattice follow the panel instead of the panel sliding across a lattice.
//
// At rest they overlap by 0.3 of their 3.4 width; sliding opens that out to
// wide double-covered bands and, at the extremes, real gaps where the light
// comes through unattenuated. Both are wanted: the varying number of layers IS
// the image.
// ORDER IS DEPTH: index 0 is the panel nearest the viewer, index 5 the
// furthest. The loop in the shader runs front to back, which is what lets a
// rear panel's lattice be dimmed by the ones in front of it.
const PANEL_COUNT = 6;
const PANELS = [
  { x: -8.2, width: 3.4, speed: 0.021, amplitude: 1.9, phase: 0.0 },
  { x: -5.1, width: 3.4, speed: 0.014, amplitude: 2.6, phase: 1.7 },
  { x: -2.0, width: 3.4, speed: 0.018, amplitude: 2.2, phase: 3.4 },
  { x: 1.1, width: 3.4, speed: 0.011, amplitude: 2.8, phase: 5.0 },
  { x: 4.2, width: 3.4, speed: 0.024, amplitude: 1.7, phase: 0.9 },
  { x: 7.3, width: 3.4, speed: 0.016, amplitude: 2.4, phase: 2.6 }
];

// --- the lattice -------------------------------------------------------
// A CELL SIZE, in world units, from which BOTH counts follow. Neither count is
// fixed: fixing the columns and deriving the rows only moved the distortion
// from one axis to the other, because a panel's width is then free to divide
// into whatever the column count happens to make it.
//
// Solved rather than guessed: on a 3.4 x 8.09 panel, 0.84 gives 4 columns and
// 9 rows - inside the wanted 4-5 by 7-9 - with cells 0.850 x 0.899, square to
// within 6%. A wider panel simply gets more columns of the same size.
const CELL_SIZE = 0.84;
const GRID_COLUMNS_MIN = 2;
const GRID_ROWS_MIN = 3;

// THICKNESSES IN PIXELS, not in fractions of the panel or of a cell.
//
// As fractions they were four different apparent thicknesses: a frame measured
// 10.6px across and 15.8px down at 1280x720, and the whole lattice scaled with
// the canvas - on the TV's 4K backing store the same fractions come out at
// 32px and 48px, which is the heavy black banding. In pixels, one number means
// one thickness, horizontally and vertically, at any resolution.
const FRAME_PX = 12;
const MUNTIN_PX = 8;
// The frame is drawn at full wood, the muntins at 0.7 of it. That ratio is the
// only thing that makes a panel read as an object rather than as more grid.
const FRAME_WOOD = 1.0;
const MUNTIN_WOOD = 0.7;

// EVERY panel crossed multiplies the light by this. One panel 0.62, two 0.38,
// three 0.24 - and nothing at all outside them, at 1.0. That cumulative
// product is the overlap; there is no separate shading term for it anywhere.
const PANEL_TRANSMISSION = 0.62;
const MIN_TRANSMISSION = 0.15;
const SAFE_ZONE_TRANSMISSION_BOOST = 1.25; // "comme si un carreau etait plus fin derriere la pochette"

const CAMERA_DIST = 6.0;
const PLANE_OVERFLOW = 1.30; // panels must clear the frame top/bottom with real margin, not just brush it

// ONE wood colour for the whole lattice; the frame and the muntins are told
// apart by FRAME_WOOD/MUNTIN_WOOD, which decide how far each is mixed toward
// it. A dark brown rather than the near-black it was: a shoji is timber, and
// pure black read as metal banding.
const WOOD_COLOR = new THREE.Color(0x241a10);
const LIGHT_COLOR_BASE = new THREE.Color(0xfff3df); // warm white
const PALETTE_TINT_MAX = 0.15;
const LUMINANCE_CAP = 0.74;
// Shader-space target, NOT the measured ceiling above - the ACES/OutputPass
// pipeline does not preserve a shader's own luminance value (the same
// finding sumi.js's and murmuration.js's own luminance work already made
// this session), so this is tuned against real rendered pixels, not derived
// analytically from LUMINANCE_CAP.
const LUMINANCE_CAP_SHADER = 0.30;

// --- reactivity ("extremement retenue") --------------------------------
const PANEL_SPEED_BAND_SWING = 0.15;   // bands.low, +-15%
const LIGHT_BAND_SWING = 0.08;         // bands.mid, +-8%
const SHADOW_DRIFT_BAND_SWING = 0.30;  // bands.high
const DYNAMICS_SWING_MIN = 0.6;
const DYNAMICS_SWING_MAX = 1.2;
// --- shadows behind the paper ------------------------------------------
// A branch is a BOUNDED OBJECT, not a field. Every previous version sampled a
// continuous noise field and thresholded it somehow - low regions gave round
// blobs, contour bands gave endless ribbons looping across the whole frame.
// Neither can ever have an end, because a field does not have ends.
//
// So a branch is now drawn explicitly: a short trunk plus two limbs, as line
// SEGMENTS with real endpoints. It occupies a bounded patch of the frame, you
// can see where it starts and where it stops, and it forks.
const SHADOW_REACH = 0.40;        // trunk length, in frame-height units
const SHADOW_LIMB_1 = 0.52;       // limbs, as a fraction of the trunk
const SHADOW_LIMB_2 = 0.44;
const SHADOW_LIMB_ANGLE_1 = 0.72; // radians off the trunk
const SHADOW_LIMB_ANGLE_2 = -0.85;
// Thickness, and the only thing depth changes: a farther branch is softer, not
// smaller (the same "profondeur par flou, pas par taille" the file started on).
const SHADOW_THICK_NEAR = 0.045;
const SHADOW_THICK_FAR = 0.080;
// Solid to this fraction of the thickness, feathering beyond - a contour you
// can follow rather than a gradient with no boundary.
const SHADOW_EDGE_START = 0.45;
// A gentle bend, so the segments are not dead straight.
const SHADOW_BEND = 0.055;
// Darkest a branch gets: diffuse, as light through paper is.
const SHADOW_FLOOR = 0.55;

const SHADOW_COUNT_MIN_FRAC = 1; // progress hump: 1 shadow at the edges...

// No-analysis fallback - slow, low-amplitude LFOs. "Le rendu est quasi
// identique": these intentionally stay close to the neutral (0.5-band)
// values rather than swinging wide the way murmuration.js's own fallback
// does, since this piece has almost no reactivity to begin with.
const FALLBACK_LFO_PERIOD_MIN_S = 50;
const FALLBACK_LFO_PERIOD_MAX_S = 70;

// shadowCountMax caps at 3 everywhere: "garde 2 ou 3 ombres maximum visibles
// simultanement". Tier 2 used to run four.
// Two shadows at once is enough - each is now three unioned filament families,
// so a layer already carries the structure two used to.
const TIER_LAYOUT = {
  0: { shadowCountMax: 2, simplifiedBlur: true },
  1: { shadowCountMax: 2, simplifiedBlur: false },
  2: { shadowCountMax: 2, simplifiedBlur: false }
};
const DEFAULT_TIER = 1;

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}
function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Own local copy of the safe-zone chunk reconstructed PER-FRAGMENT from the
// frame UV, not the shared vertex-space chunk - this is a single oversized
// quad with only 4 corner vertices, and interpolating a localised box
// across that sparse a geometry cannot place it correctly (the same lesson
// sumi.js/diorama.js/murmuration.js already ran into). Returns the raw
// signed distance (negative = inside the cover's box) rather than a
// pre-mixed attenuation, since this file uses it as a BOOST (light
// visualizers invert the rule: "eclaircis", not darken) rather than a
// straight multiply.
function safeZoneDistanceChunk() {
  return `
    uniform float uAspect;
    uniform vec2 uSafeCenter;
    uniform vec2 uSafeHalfSize;
    uniform float uSafeFeather;
    float safeZoneDistance(vec2 frameUv) {
      vec2 ndc = frameUv * 2.0 - 1.0;
      vec2 s = vec2(ndc.x * uAspect, ndc.y);
      vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
      return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    }
  `;
}

// Cheap hash/value-noise/fbm for the shadow silhouettes - same proven
// pattern as murmuration.js's own curl-noise potential, just used here as a
// plain 2D density field rather than differentiated for a curl.
function noiseChunk() {
  return `
    float shHash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
    }
    float shNoise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      float a = shHash(i), b = shHash(i + vec2(1.0, 0.0));
      float c = shHash(i + vec2(0.0, 1.0)), d = shHash(i + vec2(1.0, 1.0));
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
    }
    // octaves is a compile-time constant per call site (unrolled per tier
    // below) - a runtime-variable trip count is not portably legal in
    // GLSL ES 1.00 and would cost a branch per octave for nothing, since
    // every call site already knows its own octave count at shader-build
    // time.
    float shFbm1(vec2 p) { return shNoise(p); }
    float shFbm2(vec2 p) { return shNoise(p) * 0.65 + shNoise(p * 2.07) * 0.35; }
    float shFbm3(vec2 p) { return shNoise(p) * 0.55 + shNoise(p * 2.07) * 0.30 + shNoise(p * 4.31) * 0.15; }
  `;
}

// One shadow layer: branches behind the paper. depthFrac 0 = nearest/sharpest,
// 1 = farthest/blurriest - depth reads through BLUR (wider band), never through
// size.
//
// BRANCHES, NOT BALLOONS. Thresholding a noise field (n < k) selects its low
// REGIONS, and the regions of a smooth field are rounded blobs - which is
// exactly what it looked like. Taking a LEVEL SET instead - the band where the
// field is near its own mid-value - selects a contour, and the contours of a
// smooth field are long curving filaments that fork and rejoin. That is the
// shape of a branch, and it comes out of the same one fbm call.
//
// Frequency is 3x the old one (the shapes were about a third of the frame
// across) and the field is anisotropic, so the filaments run long rather than
// round.
function shadowLayerGlsl(index, simplified) {
  const fbmCall = simplified ? 'shFbm1' : 'shFbm2';
  return `
    float shSeg${index}(vec2 p, vec2 a, vec2 b) {
      vec2 pa = p - a, ba = b - a;
      float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
      return length(pa - ba * h);
    }

    float shadowLayer${index}(vec2 uv, float seed, float depthFrac, float driftSpeed) {
      // Aspect-corrected, so a branch is the same shape whatever the frame.
      vec2 p = vec2(uv.x * uAspect, uv.y);

      // A gentle bend on the sampling point rather than on the geometry: the
      // segments stay bounded and countable, but stop reading as drawn lines.
      p += (${fbmCall}(p * 2.4 + seed) - 0.5) * ${SHADOW_BEND.toFixed(3)};

      // Where this branch sits, drifting slowly sideways and wrapping. It is
      // ONE object in one place, not a texture over the whole frame.
      float travel = fract(seed * 0.317 + uShadowClock * driftSpeed * 0.014);
      vec2 root = vec2(travel * (uAspect + ${(2 * SHADOW_REACH).toFixed(2)}) - ${SHADOW_REACH.toFixed(2)},
                       0.12 + 0.70 * fract(seed * 0.713));
      float ang = seed * 2.39 + 1.1;

      float reach = ${SHADOW_REACH.toFixed(3)};
      vec2 dir = vec2(cos(ang), sin(ang));
      vec2 tip = root + dir * reach;

      // Trunk, then two limbs leaving it at different points and angles. Every
      // one of them ENDS.
      float d = shSeg${index}(p, root, tip);
      vec2 f1 = root + dir * reach * 0.55;
      vec2 g1 = vec2(cos(ang + ${SHADOW_LIMB_ANGLE_1.toFixed(3)}), sin(ang + ${SHADOW_LIMB_ANGLE_1.toFixed(3)}));
      d = min(d, shSeg${index}(p, f1, f1 + g1 * reach * ${SHADOW_LIMB_1.toFixed(3)}));
      vec2 f2 = root + dir * reach * 0.30;
      vec2 g2 = vec2(cos(ang + ${SHADOW_LIMB_ANGLE_2.toFixed(3)}), sin(ang + ${SHADOW_LIMB_ANGLE_2.toFixed(3)}));
      d = min(d, shSeg${index}(p, f2, f2 + g2 * reach * ${SHADOW_LIMB_2.toFixed(3)}));

      float thick = mix(${SHADOW_THICK_NEAR.toFixed(3)}, ${SHADOW_THICK_FAR.toFixed(3)}, depthFrac);
      // Tapered: thinner toward the tips, as a branch is.
      float along = clamp(length(p - root) / reach, 0.0, 1.0);
      thick *= mix(1.0, 0.45, along);

      float m = 1.0 - smoothstep(thick * ${SHADOW_EDGE_START.toFixed(2)}, thick, d);
      return mix(${SHADOW_FLOOR.toFixed(3)}, 1.0, 1.0 - m);
    }
  `;
}

function shojiFragmentShader(shadowCountMax, simplifiedBlur) {

  const shadowFns = [];
  for (let s = 0; s < shadowCountMax; s++) shadowFns.push(shadowLayerGlsl(s, simplifiedBlur));

  const shadowCalls = [];
  for (let s = 0; s < shadowCountMax; s++) {
    const depthFrac = shadowCountMax > 1 ? s / (shadowCountMax - 1) : 0;
    const seed = (s * 41.7 + 3.1).toFixed(2);
    const driftMul = (0.55 + s * 0.22).toFixed(3); // each layer drifts at its own fraction of the shared speed
    shadowCalls.push(`
    {
      float vis = 1.0 - smoothstep(uShadowVisible - 1.0, uShadowVisible, ${s}.0);
      float l = shadowLayer${s}(fUv, ${seed}, ${depthFrac.toFixed(3)}, ${driftMul});
      shadow = min(shadow, mix(1.0, l, vis));
    }`);
  }

  return `
    precision mediump float;
    varying vec2 vUv;

    uniform float uTime;
    uniform float uShadowClock;
    uniform vec2 uResolution;
    uniform float uShadowVisible;
    uniform float uLightIntensity;
    uniform vec3 uLightColor;
    uniform vec3 uWoodColor;
    // The six panels' current [xLeft, xRight], in WORLD units. Six vec2, and
    // the only thing this shader knows about where the panels are.
    uniform vec2 uPanneaux[${PANEL_COUNT}];
    uniform float uFrameWidth;
    uniform float uFrameHeight;
    uniform float uGridDebug;
    uniform float uLayer;
    uniform sampler2D uPaper;
    // The texture's own mean, in the LINEAR light a shader actually samples -
    // see paper-texture.js's sampling-convention note. Both grain curves below
    // are centred on THIS rather than on a written-down reference, so the
    // deviation they amplify is a real deviation from this texture's own
    // density and a different paper cannot silently drive them into their
    // clamp. 0 means the measurement failed; the curves fall back to 1.0,
    // which is no grain but also no darkening - the failure mode has to be
    // "flat", never "35% dimmer".
    uniform float uPaperMean;

    ${safeZoneDistanceChunk()}
    ${noiseChunk()}
    ${shadowFns.join('\n')}

    void main() {
      // "Frame UV": vUv spans the OVERSIZED plane (PLANE_OVERFLOW beyond the
      // frustum), remapped so 0..1 covers exactly the visible frame and the
      // panels/light/shadows are laid out and measured against what a
      // viewer actually sees, not the plane's own inflated extent.
      vec2 fUv = (vUv - 0.5) * ${PLANE_OVERFLOW.toFixed(3)} + 0.5;

      float safeDist = safeZoneDistance(fUv);
      float safeBoost = mix(${SAFE_ZONE_TRANSMISSION_BOOST.toFixed(3)}, 1.0, smoothstep(0.0, uSafeFeather, safeDist));

      // Light source - a soft radial falloff around a very slowly drifting
      // centre.
      vec2 lightCenter = vec2(0.5, 0.55) + vec2(sin(uTime * 0.021), cos(uTime * 0.017)) * 0.12;
      vec2 ld = (fUv - lightCenter) * vec2(uAspect, 1.0);
      float lightFalloff = 1.0 - smoothstep(0.15, 0.95, length(ld));
      vec3 light = uLightColor * uLightIntensity * mix(0.55, 1.0, lightFalloff);

      // Shadows - unrolled per tier, darkest layer wins where they overlap.
      float shadow = 1.0;
      ${shadowCalls.join('\n')}

      vec3 litBehind = light * shadow;

      // --- THE PANELS ---------------------------------------------------
      // World x across the visible frame, and a 0..1 height. Panels are
      // positioned in these units on the CPU (see PANELS), so nothing here
      // depends on the screen.
      float worldX = (fUv.x - 0.5) * uFrameWidth;
      float worldY01 = fUv.y;

      float transmission = 1.0;
      float bois = 0.0;

      for (int pi = 0; pi < ${PANEL_COUNT}; pi++) {
        vec2 bornes = uPanneaux[pi];
        if (worldX < bornes.x || worldX > bornes.y) continue;

        // LOCAL to this panel, 0 at its left edge and 1 at its right, so the
        // lattice travels WITH the panel. This is the whole reason the grid
        // stays regular: nothing below ever sees a screen coordinate.
        float u = (worldX - bornes.x) / (bornes.y - bornes.x);
        float v = worldY01;

        // SQUARE CELLS BY CONSTRUCTION. One cell size in world units, and
        // both counts fall out of it - so a cell is as wide as it is tall
        // whatever the panel's width, and a wider panel just gets more
        // columns.
        float largeurPanneau = bornes.y - bornes.x;
        float colonnes = max(${GRID_COLUMNS_MIN.toFixed(1)},
                             floor(largeurPanneau / ${CELL_SIZE.toFixed(3)}));
        float rangees = max(${GRID_ROWS_MIN.toFixed(1)},
                            floor(uFrameHeight / ${CELL_SIZE.toFixed(3)}));

        // Member thicknesses converted from PIXELS into this panel's own
        // local units, so horizontal and vertical members are the same
        // apparent thickness and stay that thickness at any resolution.
        float panelWpx = ((bornes.y - bornes.x) / uFrameWidth) * uResolution.x;
        float panelHpx = uResolution.y;
        float frameU = ${FRAME_PX.toFixed(1)} / max(panelWpx, 1.0);
        float frameV = ${FRAME_PX.toFixed(1)} / max(panelHpx, 1.0);

        // Peripheral frame.
        float cadre = step(u, frameU) + step(1.0 - frameU, u)
                    + step(v, frameV) + step(1.0 - frameV, v);

        // Interior lattice - identical cells by construction, since they are
        // just fract() of the same local coordinate.
        vec2 cell = vec2(u, v) * vec2(colonnes, rangees);
        vec2 f = fract(cell);
        // One member at the leading edge of each cell, not one on each side:
        // two half-members meeting at a boundary made every interior division
        // twice the intended thickness. The division at u=0 falls inside the
        // frame and is simply covered by it.
        float mx = (${MUNTIN_PX.toFixed(1)} / max(panelWpx, 1.0)) * colonnes;
        float my = (${MUNTIN_PX.toFixed(1)} / max(panelHpx, 1.0)) * rangees;
        float montant = step(f.x, mx) + step(f.y, my);

        // The step() sums reach 2 at a corner; clamped so a corner is not
        // twice as woody as an edge.
        float woodHere = max(min(cadre, 1.0) * ${FRAME_WOOD.toFixed(2)},
                             min(montant, 1.0) * ${MUNTIN_WOOD.toFixed(2)});

        // A REAR panel's lattice is seen THROUGH the ones in front of it, so it
        // is dimmed by exactly the light they let past. Index 0 is nearest, so
        // at this point transmission already holds the product of every panel
        // nearer than this one - which is precisely that factor.
        //
        // Without it every overlapping panel draws its lattice at full
        // strength, two grids land on the same pixels, and the result reads as
        // one irregular lattice with members that stop halfway - the exact
        // defect this rebuild is for. With it the front panel's grid is crisp
        // and black, the one behind recedes to 62% and reads as behind.
        bois = max(bois, woodHere * transmission);

        // EVERY panel crossed attenuates. This cumulative product IS the
        // overlap - there is no other term for it.
        transmission *= ${PANEL_TRANSMISSION.toFixed(3)};
      }

      transmission = max(transmission, ${MIN_TRANSMISSION.toFixed(3)});

      // Taken BEFORE the safe-zone boost, so the isolation view below shows
      // the panel product alone and not a soft blob washed across the middle.
      float transmissionRaw = transmission;

      // The cover's own zone reads as if the paper there were thinner.
      transmission = min(transmission * safeBoost, 1.0);

      // --- LAYER ISOLATION ----------------------------------------------
      // ?shojilayer=transmission|shadow|wood|paper - one layer at a time, in
      // grey, with nothing else composited. Six rounds of adjusting the final
      // image produced six regressions because there was no way to see which
      // stage had broken; these are that way.
      //
      // Each tap is the SAME variable the composition below consumes, read at
      // the point it is consumed - not a recomputation. A layer that looks
      // right here and is invisible in the composite is an ordering or
      // zero-factor problem, not a broken layer.
      vec3 layerPaper = texture2D(uPaper, fract(fUv * 40.0)).rgb;
      float layerGrain = uPaperMean > 0.0
        ? clamp(1.0 + (layerPaper.r - uPaperMean) * 14.0, 0.65, 1.35)
        : 1.0;
      if (uLayer > 0.5) {
        vec3 outc = vec3(0.0);
        // 1 - transmission: the cumulative panel product, before the safe zone.
        if (uLayer < 1.5) outc = vec3(transmissionRaw);
        // 2 - shadow: the field as composed, 1.0 = clear, floor = darkest.
        else if (uLayer < 2.5) outc = vec3(shadow);
        // 3 - wood: the lattice mask.
        else if (uLayer < 3.5) outc = vec3(bois);
        // 4 - paper: the grain AS APPLIED, stretched from its 0.65..1.35
        //     working range onto 0..1 so it can be seen. Showing the raw
        //     texture sample instead would be misleading: papier.webp was
        //     built deliberately uniform for sumi.js (stddev ~0.012 about a
        //     mean of ~0.92), so it reads as flat grey whether or not it is
        //     bound - it is the amplification below that turns it into grain,
        //     and that is what either reaches the image or does not.
        else outc = vec3(clamp((layerGrain - 0.65) / 0.70, 0.0, 1.0));
        gl_FragColor = vec4(outc, 1.0);
        return;
      }

      // --- surface ------------------------------------------------------
      // Shadow onto the PAPER first, then wood over the top with a MASK - so
      // the wood is opaque and no shadow can cross it. Mixing the wood in by
      // bois alone left a muntin 30% transparent and a rear panel's frame
      // 38%, and the shadow behind showed straight through both. A shadow
      // lies behind the paper; it cannot fall on the lattice in front of it.
      // layerPaper/layerGrain above are the same sample and the same curve -
      // reused rather than fetched twice, so the isolation view is guaranteed
      // to be showing exactly what the composition consumes.
      float grain = layerGrain;
      vec3 paperLit = litBehind * transmission * grain;
      vec3 col = paperLit;

      if (bois > 0.0) {
        vec3 wood = texture2D(uPaper, fract(fUv * 6.0 + 11.0)).rgb;
        // Same miscalibration, same fix: this one was pinned at 0.75, so the
        // lattice carried no grain either and was uniformly 25% dark.
        float wg = uPaperMean > 0.0
          ? clamp(1.0 + (wood.r - uPaperMean) * 10.0, 0.75, 1.20)
          : 1.0;
        // bois still sets the member's VALUE - a frame at 1.0 lands fully on
        // the wood colour, a muntin at 0.7 stops short and stays lighter, a
        // rear panel's lattice stops shorter still - but it is blended against
        // UNSHADOWED light, so what shows through is paper, never a branch.
        vec3 unshadowed = light * transmission * grain;
        vec3 woodTone = mix(unshadowed, uWoodColor * wg, bois);
        col = mix(col, woodTone, step(0.001, bois));
      }

      if (uGridDebug > 0.5) {
        // Each panel in its own hue, its frame solid - to confirm the six are
        // distinct objects and that they slide.
        // The NEAREST coverer - the one whose lattice is actually drawn - so
        // the debug hue names the panel you are looking at.
        float idx = -1.0;
        for (int pi = 0; pi < ${PANEL_COUNT}; pi++) {
          vec2 bornes = uPanneaux[pi];
          if (idx < 0.0 && worldX >= bornes.x && worldX <= bornes.y) idx = float(pi);
        }
        if (idx >= 0.0) {
          vec3 hue = 0.5 + 0.5 * cos(6.2831853 * (idx / ${PANEL_COUNT}.0)
                                     + vec3(0.0, 2.094, 4.188));
          col = mix(col, hue, bois > 0.5 ? 0.95 : 0.40);
        }
      }

      // Ceiling with a SOFT knee, not a hard clip. The hard version pinned
      // everything above the cap to exactly the cap, and the brightest part of
      // the frame is precisely where the panels overlap most - so measured
      // across five overlap boundaries in the middle of the picture, a
      // two-layer region and a one-layer region came out at the identical
      // 170.8/255 and the overlap was completely invisible there. That is the
      // one cue that the panels are sliding at all.
      //
      // 1 - exp(-x) is monotonic and asymptotic to the cap: nothing ever
      // exceeds it (the reason the cap exists - see LUMINANCE_CAP's note about
      // the bloom threshold), but two different inputs never come out equal,
      // so the doubly-attenuated zones stay readably darker.
      //
      // The light level itself was left alone. Dimming the lamp so a single
      // sheet landed under the cap was the other candidate fix, and it was
      // swept and measured (scales 1.0 / 0.8 / 0.65 / 0.5): overlap contrast
      // stayed flat at 83-86% across the whole range while mean picture
      // brightness fell from 90 to 63. The knee was doing the work; dimming
      // would only have cost brightness.
      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      const float LUM_CAP = ${LUMINANCE_CAP_SHADER.toFixed(3)};
      float rolled = LUM_CAP * (1.0 - exp(-lum / LUM_CAP));
      col *= rolled / max(lum, 0.0001);

      gl_FragColor = vec4(col, 1.0);
    }
  `;
}

const shojiVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Tiny 1x1 fallback so a not-yet-loaded/missing paper texture degrades to a
// flat mid-tone instead of a broken sampler - same convention as sumi.js's/
// murmuration.js's own fallback textures.
let _fallbackPaperTexture = null;
function fallbackPaperTexture() {
  if (!_fallbackPaperTexture) {
    _fallbackPaperTexture = new THREE.DataTexture(new Uint8Array([190, 190, 190, 255]), 1, 1, THREE.RGBAFormat);
    _fallbackPaperTexture.needsUpdate = true;
  }
  return _fallbackPaperTexture;
}

export class ShojiVisualizer extends Visualizer {
  constructor(options) {
    super();
    const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    const forcedTier = parseInt(params.get('shojitier'), 10);
    const tier = Number.isFinite(forcedTier) && TIER_LAYOUT[forcedTier] != null
      ? forcedTier
      : (options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER);
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    // ?shojigrid=1 - see the shader's own uGridDebug block.
    this._gridDebug = params.get('shojigrid') === '1';
    // ?shojiflat=1 - transmission alone, in grey. The first thing to get right.
    // ?shojilayer=transmission|shadow|wood|paper - see the shader's own
    // LAYER ISOLATION block. ?shojiflat=1 stays as an alias for the
    // transmission layer.
    const LAYERS = { transmission: 1, shadow: 2, wood: 3, paper: 4 };
    const layerName = params.get('shojilayer');
    this._layer = LAYERS[layerName] || (params.get('shojiflat') === '1' ? 1 : 0);
    this._paperTexture = options && options.paperTexture !== undefined ? options.paperTexture : getPaperTexture();
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);

    const layout = TIER_LAYOUT[this._tier];
    this._shadowCountMax = layout.shadowCountMax;

    this._time = 0;
    this._panelClock = 0;
    this._shadowClock = 0;
    this._paused = false;

    // Current [xLeft, xRight] per panel, in world units - rewritten in place
    // every frame and handed straight to the shader. Allocated once.
    this._bornes = [];
    for (let i = 0; i < PANEL_COUNT; i++) this._bornes.push(new THREE.Vector2(0, 0));
    this._updateBornes();

    // No-analysis fallback LFO periods, one per band, drawn once.
    this._fallbackPeriod = [0, 0, 0].map(() => lerp(FALLBACK_LFO_PERIOD_MIN_S, FALLBACK_LFO_PERIOD_MAX_S, Math.random()));

    this._lastPaletteRef = undefined;
    this._lightColorFrom = LIGHT_COLOR_BASE.clone();
    this._lightColorTo = LIGHT_COLOR_BASE.clone();
    this._paletteLerpStart = -Infinity;

    this._buildMesh();
  }

  _buildMesh() {
    const fovRad = THREE.MathUtils.degToRad(this._camera.fov);
    const frustumHeight = 2 * CAMERA_DIST * Math.tan(fovRad / 2);
    const frustumWidth = frustumHeight * this._camera.aspect;
    const geometry = new THREE.PlaneGeometry(frustumWidth * PLANE_OVERFLOW, frustumHeight * PLANE_OVERFLOW);

    const fragmentShader = shojiFragmentShader(this._shadowCountMax, TIER_LAYOUT[this._tier].simplifiedBlur);
    // The visible frame's width in world units - what worldX is measured in.
    this._frameWidth = frustumWidth;

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uShadowClock: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uShadowVisible: { value: this._shadowCountMax },
      uLightIntensity: { value: 1 },
      uLightColor: { value: this._lightColorTo },
      uWoodColor: { value: WOOD_COLOR.clone() },
      uPanneaux: { value: this._bornes },
      uFrameWidth: { value: frustumWidth },
      uFrameHeight: { value: frustumHeight },
      uLayer: { value: this._layer },
      uGridDebug: { value: this._gridDebug ? 1 : 0 },
      uPaper: { value: this._paperTexture || fallbackPaperTexture() },
      uPaperMean: { value: this._paperTexture ? getPaperMean() : 0 }
    });

    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: shojiVertexShader,
      fragmentShader,
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.FrontSide
    });
    this._geometry = geometry;
    this._mesh = new THREE.Mesh(geometry, this._material);
    this._mesh.position.set(0, 0, 0);
    this._scene.add(this._mesh);
  }

  // Each panel's current world bounds. Position is its rest x plus a slow
  // independent sine - nothing here depends on the canvas, so a resize cannot
  // move a panel or bend its lattice.
  _updateBornes() {
    for (let i = 0; i < PANEL_COUNT; i++) {
      const d = PANELS[i];
      const offset = d.amplitude * Math.sin(2 * Math.PI * d.speed * this._panelClock + d.phase);
      this._bornes[i].set(d.x + offset, d.x + offset + d.width);
    }
  }

  _updatePaletteColor(profile) {
    const palette = profile.artworkPalette;
    const dominant = palette && palette.dominantColors;
    const ref = dominant && dominant.length > 0 ? dominant[0] : null;
    if (ref !== this._lastPaletteRef) {
      this._lastPaletteRef = ref;
      this._lightColorFrom.copy(this._lightColorTo);
      const target = LIGHT_COLOR_BASE.clone();
      if (ref) target.lerp(new THREE.Color(ref), PALETTE_TINT_MAX);
      this._lightColorToTarget = target;
      this._paletteLerpStart = this._time;
    }
    const t = clamp01((this._time - this._paletteLerpStart) / 0.9);
    if (this._lightColorToTarget) {
      this._uniforms.uLightColor.value.copy(this._lightColorFrom).lerp(this._lightColorToTarget, t);
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    // Written BEFORE the pause return: it is not animation, it is the scale
    // the muntin and frame thicknesses are measured against, and a window
    // resize while paused would otherwise leave them wrong until playback
    // resumed.
    const dprNow = Math.min(window.devicePixelRatio || 1, 2);
    const resX = window.innerWidth * dprNow;
    const resY = window.innerHeight * dprNow;
    this._uniforms.uResolution.value.set(resX, resY);
    if (paused) return; // full freeze - "gel complet"

    this._time += deltaTime;

    const director = profile.director;
    const bands = (director && director.bands) || null;
    const hasGrid = !!(director && director.hasReliableGrid && bands);
    const character = (director && director.character) || null;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    const dynScale = lerp(DYNAMICS_SWING_MIN, DYNAMICS_SWING_MAX, dynamics);

    let bandsLow, bandsMid, bandsHigh;
    if (hasGrid) {
      bandsLow = bands.low;
      bandsMid = bands.mid;
      bandsHigh = bands.high;
    } else {
      bandsLow = 0.5 + 0.5 * Math.sin((2 * Math.PI * this._time) / this._fallbackPeriod[0]);
      bandsMid = 0.5 + 0.5 * Math.sin((2 * Math.PI * this._time) / this._fallbackPeriod[1] + 1.9);
      bandsHigh = 0.5 + 0.5 * Math.sin((2 * Math.PI * this._time) / this._fallbackPeriod[2] + 4.1);
    }

    const panelSpeedMul = 1 + (bandsLow - 0.5) * 2 * PANEL_SPEED_BAND_SWING * dynScale;
    const lightIntensityMul = 1 + (bandsMid - 0.5) * 2 * LIGHT_BAND_SWING * dynScale;
    const shadowDriftMul = 1 + (bandsHigh - 0.5) * 2 * SHADOW_DRIFT_BAND_SWING * dynScale;

    this._panelClock += deltaTime * panelSpeedMul;
    this._shadowClock += deltaTime * shadowDriftMul;

    this._updateBornes();

    // Shadow count - a hump, 1 at both ends of the track, the tier's max at
    // the middle. Same "arrive and leave" shape as murmuration.js's own
    // active-boid count.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    this._uniforms.uShadowVisible.value = 1 + (this._shadowCountMax - 1) * Math.sin(Math.PI * progress);

    this._uniforms.uLightIntensity.value = clamp(lightIntensityMul, 0.5, 1.5);
    this._updatePaletteColor(profile);

    this._uniforms.uTime.value = this._time;
    this._uniforms.uShadowClock.value = this._shadowClock;
  }

  onResize() {
    if (!this._camera || !this._mesh) return;
    const fovRad = THREE.MathUtils.degToRad(this._camera.fov);
    const frustumHeight = 2 * CAMERA_DIST * Math.tan(fovRad / 2);
    const frustumWidth = frustumHeight * this._camera.aspect;
    this._mesh.geometry.dispose();
    this._mesh.geometry = new THREE.PlaneGeometry(frustumWidth * PLANE_OVERFLOW, frustumHeight * PLANE_OVERFLOW);
    // worldX is measured against the visible frame, so this has to follow it.
    this._frameWidth = frustumWidth;
    if (this._uniforms) {
      this._uniforms.uFrameWidth.value = frustumWidth;
      this._uniforms.uFrameHeight.value = frustumHeight;
    }
  }

  dispose() {
    this._scene.remove(this._mesh);
    this._geometry.dispose();
    this._material.dispose();
    // uPaper is a shared, preloaded asset (see paper-texture.js) - never
    // disposed here. The 1x1 fallback texture is a shared module-level
    // singleton too.
  }
}
